import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BudgetState } from '../../components/BuyerPrep/tips/types';

const initialState: BudgetState = {
  grossMonthlyIncome: 5500,
  monthlyDebts: 450,
  downPayment: 35000,
  interestRate: 7.0,
  loanTerm: 30,
  selectedHomePrice: 300000,
  monthlyCosts: {
    principalAndInterest: 0,
    propertyTax: 0,
    insurance: 0,
    utilities: 200,
    maintenance: 0,
    total: 0,
  },
};

const calculateMonthlyCosts = (
  state: BudgetState,
  homePrice: number = state.selectedHomePrice
) => {
  const downPayment = state.downPayment;
  const loanAmount = homePrice - downPayment;

  // Monthly principal and interest
  const monthlyRate = state.interestRate / 100 / 12;
  const numPayments = state.loanTerm * 12;
  const monthlyPI =
    (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
    (Math.pow(1 + monthlyRate, numPayments) - 1);

  // Other monthly costs
  const propertyTax = (homePrice * 0.01) / 12; // 1% annual property tax
  const insurance = (homePrice * 0.003) / 12; // 0.3% annual insurance
  const utilities = 200; // Base utility estimate
  const maintenance = (homePrice * 0.01) / 12; // 1% annual maintenance fund

  return {
    principalAndInterest: monthlyPI,
    propertyTax,
    insurance,
    utilities,
    maintenance,
    total: monthlyPI + propertyTax + insurance + utilities + maintenance,
  };
};

const budgetSlice = createSlice({
  name: 'budget',
  initialState,
  reducers: {
    updateBudgetState: (state, action: PayloadAction<Partial<BudgetState>>) => {
      Object.assign(state, action.payload);
      if (action.payload.selectedHomePrice) {
        state.monthlyCosts = calculateMonthlyCosts(
          state,
          action.payload.selectedHomePrice
        );
      }
    },
    calculateMonthlyCostsForPrice: (state, action: PayloadAction<number>) => {
      state.selectedHomePrice = action.payload;
      state.monthlyCosts = calculateMonthlyCosts(state, action.payload);
    },
  },
});

export const { updateBudgetState, calculateMonthlyCostsForPrice } =
  budgetSlice.actions;
export default budgetSlice.reducer;
